import React from 'react';
import { ArrowUpRight, Database, Plus } from 'lucide-react';

import type { SchemaDefinition } from '../../lib/api';
import { DOC_LINKS, getCategoryLabel } from './helpers';
import styles from './schema-editor.module.css';

interface SchemaEmptyStateProps {
  schemas: SchemaDefinition[];
  onSelect: (schemaId: string) => void;
  onCreate: () => void;
}

const NO_SCHEMAS_TITLE = 'No schemas yet';
const NO_SCHEMAS_BODY =
  'Schemas describe the records that live in your platform — customers, transactions, locations, and anything custom. Create one to map its fields to Reach so they can power attribution, segments, messages, and agents.';
const PICK_SCHEMA_TITLE = 'Pick a schema to view and edit';
const PICK_SCHEMA_BODY =
  'Choose one of your schemas below to review its fields, mappings, and what is exposed to segments, merge fields, and agents.';

/** Short "{Category} · v{n}" caption shown under each schema name. */
function schemaCaption(schema: SchemaDefinition): string {
  const version = schema.versionNumber ?? schema.version_number;
  const category = getCategoryLabel(schema.category);
  return version != null ? `${category} · v${version}` : category;
}

/**
 * Full-width placeholder shown when no schema is selected. Doubles as the
 * onboarding surface: a create prompt, the partner's existing schemas as
 * clickable cards, and setup-guide links.
 */
export const SchemaEmptyState: React.FC<SchemaEmptyStateProps> = ({ schemas, onSelect, onCreate }) => {
  const hasSchemas = schemas.length > 0;
  const title = hasSchemas ? PICK_SCHEMA_TITLE : NO_SCHEMAS_TITLE;
  const body = hasSchemas ? PICK_SCHEMA_BODY : NO_SCHEMAS_BODY;

  return (
    <div className={styles.emptyStateWrapper}>
      <section className={styles.emptyStateHero}>
        <span className={styles.emptyStateHeroIcon}>
          <Database size={20} />
        </span>
        <h2 className={styles.emptyStateHeroTitle}>{title}</h2>
        <p className={styles.emptyStateHeroBody}>{body}</p>
        <div className={styles.emptyStateHeroActions}>
          <button type="button" className={styles.emptyStatePrimaryButton} onClick={onCreate}>
            <Plus size={15} />
            Create schema
          </button>
          {hasSchemas && (
            <button
              type="button"
              className={styles.emptyStateSecondaryButton}
              onClick={() => onSelect(schemas[0].id)}>
              Browse your schemas
            </button>
          )}
        </div>
      </section>

      {hasSchemas && (
        <section className={styles.emptyStateGroup}>
          <span className={styles.emptyStateGroupEyebrow}>Your schemas</span>
          <div className={styles.emptyStateCardGrid}>
            {schemas.map(schema => (
              <button
                key={schema.id}
                type="button"
                className={styles.emptyStateSchemaCard}
                onClick={() => onSelect(schema.id)}>
                <span className={styles.emptyStateSchemaName}>{schema.name}</span>
                <span className={styles.emptyStateSchemaSub}>{schemaCaption(schema)}</span>
                {schema.description && (
                  <span className={styles.emptyStateSchemaBlurb}>{schema.description}</span>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className={styles.emptyStateGroup}>
        <span className={styles.emptyStateGroupEyebrow}>Start with a guide</span>
        <div className={styles.emptyStateCardGrid}>
          {DOC_LINKS.map(link => (
            <a
              key={link.title}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.emptyStateDocCard}>
              <span className={styles.emptyStateDocTitleLine}>
                <span className={styles.emptyStateDocTitle}>{link.title}</span>
                <ArrowUpRight size={13} className={styles.emptyStateDocIcon} />
              </span>
              <span className={styles.emptyStateDocSub}>{link.description}</span>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
};
